// Instruments — live OPEN-market list joined with a preset's instrument
// selection. Operators pick which preset to scope (default = the
// is_default preset), flip between "all markets in this preset's mode"
// and "only checked markets", and toggle individual checkboxes. Each
// change PATCHes /admin/mm/presets/{id}/instruments and the runner
// picks up the new selection at the next discovery tick of any *future*
// run started from that preset. Edits do not affect a currently-running
// run — its preset_snapshot is frozen at start time.
//
// SM-525 — the page is MODE-SCOPED, not mode-aware-with-a-filter:
//
//   * The preset's own trading mode decides which markets the picker
//     may show, and the list is narrowed SERVER-SIDE by that mode.
//   * The bulk action is "Select all Cash markets" or "Select all Game
//     markets" per that mode. There is deliberately no cross-mode
//     "Select all markets" button, because there is no cross-mode
//     preset for it to write into.
//   * Bulk selection persists an explicit SNAPSHOT of the ids listed at
//     the moment it was clicked. It is not a standing rule, so a market
//     published tomorrow does not silently join a preset an operator
//     reviewed today.
import { createFileRoute } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { invalidateAffected } from "@/lib/routeRefresh";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  TradingModeBadge,
  TradingModeCurrencies,
  tradingModeLabel,
} from "@/components/mode/TradingModeBadge";
import {
  keys,
  fetchInstruments,
  fetchPresets,
  fetchPreset,
  updatePresetInstruments,
} from "@/lib/queries";
import { effectiveTradingMode, marketAllowsMode, marketModes } from "@/lib/tradingMode";
import type { MMMarketSelection, MMPreset } from "@/lib/types";
import { formatPrice } from "@/lib/money";
import { useI18n } from "@/i18n/I18nProvider";

export const Route = createFileRoute("/_app/instruments")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.instruments),
  component: InstrumentsPage,
});

function InstrumentsPage() {
  const { t, formatDateTime } = useI18n();
  const qc = useQueryClient();

  // Active preset list. We don't paginate here because the selector
  // typically lists 1–10 presets, and this is the only place that
  // reads them in this page.
  const presetsQ = useQuery({
    queryKey: keys.presets(100, 0),
    queryFn: () => fetchPresets(100, 0),
  });

  // The selected preset_id drives the editing surface. Default to the
  // is_default preset on first load; fall back to the first row.
  const [selectedPresetID, setSelectedPresetID] = useState<string | null>(null);
  useEffect(() => {
    if (selectedPresetID || !presetsQ.data) return;
    const def = presetsQ.data.presets.find((p) => p.is_default);
    setSelectedPresetID(def?.preset_id ?? presetsQ.data.presets[0]?.preset_id ?? null);
  }, [presetsQ.data, selectedPresetID]);

  const presetQ = useQuery({
    queryKey: selectedPresetID ? keys.preset(selectedPresetID) : ["mm", "preset", "unset"],
    queryFn: () => fetchPreset(selectedPresetID!),
    enabled: !!selectedPresetID,
  });

  const preset = presetQ.data;
  // The preset's mode is the page's scope. Read it from the row's
  // denormalised field when present, otherwise fold the config default —
  // one helper, so a legacy preset with no mode reads as CASH here
  // exactly as it does on the server.
  const presetMode = preset?.trading_mode ?? effectiveTradingMode(preset?.config);

  // The catalog is fetched FOR that mode. Filtering a full list in the
  // browser instead would mean the ineligible rows exist in the page and
  // one missed guard makes them selectable.
  const instrumentsQ = useQuery({
    queryKey: keys.instruments(presetMode),
    queryFn: () => fetchInstruments(presetMode),
    refetchInterval: 30_000,
    enabled: !!preset,
  });

  const mut = useMutation({
    mutationFn: ({ presetID, selection }: { presetID: string; selection: MMMarketSelection }) =>
      updatePresetInstruments(presetID, selection),
    // Optimistic update so the checkbox feels instant; the response
    // (which echoes the normalized selection back) replaces our local
    // guess on success.
    onMutate: async ({ presetID, selection }) => {
      await qc.cancelQueries({ queryKey: keys.preset(presetID) });
      const prev = qc.getQueryData<MMPreset>(keys.preset(presetID));
      if (prev) {
        qc.setQueryData<MMPreset>(keys.preset(presetID), {
          ...prev,
          config: { ...prev.config, markets: selection },
        });
      }
      return { prev };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(keys.preset(vars.presetID), ctx.prev);
    },
    onSuccess: (p) => {
      qc.setQueryData(keys.preset(p.preset_id), p);
      invalidateAffected(qc, "instruments");
    },
  });

  const selection = preset?.config.markets;
  const selectedSet = useMemo(
    () => new Set(selection?.allowlist ?? []),
    [selection?.allowlist],
  );
  const selectionMode = selection?.mode ?? "all";
  const allowlistMode = selectionMode === "allowlist";

  const listed = instrumentsQ.data?.markets ?? [];
  // Second guard behind the server's narrowing: it covers the window
  // between switching preset and the refetch landing, when the cache
  // still holds the previous mode's rows.
  const eligible = useMemo(
    () => listed.filter((i) => marketAllowsMode(i, presetMode)),
    [listed, presetMode],
  );
  const excludedCount = listed.length - eligible.length;

  function applySelectionMode(next: "all" | "allowlist") {
    if (!preset || !selection || mut.isPending) return;
    if (next === selectionMode) return;
    mut.mutate({
      presetID: preset.preset_id,
      selection: { mode: next, allowlist: next === "allowlist" ? selection.allowlist : [] },
    });
  }

  function toggleInstrument(instrumentID: string) {
    if (!preset || !selection || mut.isPending) return;
    const present = selectedSet.has(instrumentID);
    const next = present
      ? selection.allowlist.filter((id) => id !== instrumentID)
      : [...selection.allowlist, instrumentID];
    mut.mutate({
      presetID: preset.preset_id,
      selection: { mode: "allowlist", allowlist: next },
    });
  }

  /**
   * "Select all <mode> markets".
   *
   * Writes the eligible ids as an explicit allowlist SNAPSHOT rather
   * than switching the preset to the live `all` selector. The two look
   * identical the moment they are clicked and diverge the moment a new
   * market is published: the snapshot keeps quoting what the operator
   * reviewed, the live selector silently grows.
   */
  function selectAllForMode() {
    if (!preset || mut.isPending || eligible.length === 0) return;
    mut.mutate({
      presetID: preset.preset_id,
      selection: { mode: "allowlist", allowlist: eligible.map((i) => i.market_id) },
    });
  }

  function clearAll() {
    if (!preset || mut.isPending) return;
    mut.mutate({
      presetID: preset.preset_id,
      selection: { mode: "allowlist", allowlist: [] },
    });
  }

  const modeName = tradingModeLabel(presetMode, t);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="page-title">{t("instruments.title")}</h1>
        <p className="page-subtitle">{t("instruments.subtitle")}</p>
      </header>
      <DataFreshness query={instrumentsQ} relatedQueries={[presetsQ, presetQ]} />

      <Card className="section-card">
        <div className="section-head flex flex-wrap items-center gap-4">
          <div className="field !mb-0">
            <label>{t("instruments.preset")}</label>
            <select
              value={selectedPresetID ?? ""}
              onChange={(e) => setSelectedPresetID(e.target.value || null)}
              disabled={!presetsQ.data}
            >
              {(presetsQ.data?.presets ?? []).map((p) => (
                <option key={p.preset_id} value={p.preset_id}>
                  {p.name}
                  {p.is_default ? `  (${t("common.default")})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* The preset's mode is shown, never chosen here: it is a
              property of the preset (edit it in the preset editor), and
              offering it as a picker on this page would imply an
              instrument list can span modes. */}
          <div className="field !mb-0">
            <label>{t("instruments.presetMode")}</label>
            <div className="flex items-center gap-2 pt-1">
              <TradingModeBadge mode={presetMode} />
              <TradingModeCurrencies mode={presetMode} />
            </div>
          </div>

          <div className="field !mb-0">
            <label>{t("instruments.mode")}</label>
            <select
              value={selectionMode}
              onChange={(e) => applySelectionMode(e.target.value as "all" | "allowlist")}
              disabled={!preset || mut.isPending}
            >
              {/* Scoped to the preset's mode — there is no cross-mode
                  "all markets" option. */}
              <option value="all">{t("editor.selectAllForMode", { mode: modeName })}</option>
              <option value="allowlist">{t("instruments.allowlist")}</option>
            </select>
          </div>

          {allowlistMode && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Badge tone="accent">
                {t("instruments.selected", { count: selection?.allowlist.length ?? 0 })}
              </Badge>
              <Badge tone="neutral">
                {t("instruments.eligibleCount", { count: eligible.length, mode: modeName })}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={selectAllForMode}
                disabled={mut.isPending || eligible.length === 0}
              >
                {presetMode === "GAME"
                  ? t("instruments.selectAllGame")
                  : t("instruments.selectAllCash")}
              </Button>
              <Button size="sm" variant="ghost" onClick={clearAll} disabled={mut.isPending}>
                {t("instruments.clearSelection")}
              </Button>
            </div>
          )}
        </div>

        {allowlistMode && (
          <div className="px-6 pb-3 text-xs t-mute">
            {t("instruments.snapshotNote")}
            {excludedCount > 0 && (
              <span className="ml-1">
                {t("instruments.excludedNote", { count: excludedCount, mode: modeName })}
              </span>
            )}
          </div>
        )}

        <div className="p-0">
          {(instrumentsQ.isLoading || presetQ.isLoading) && (
            <div className="p-6">
              <Skeleton lines={6} />
            </div>
          )}
          {instrumentsQ.data && eligible.length === 0 && (
            <div className="empty m-6">
              {t("instruments.noneForMode", { mode: modeName })}
            </div>
          )}
          {eligible.length > 0 && (
            <table className="tbl rounded-none border-0">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>{t("instruments.instrument")}</th>
                  <th>{t("mode.column")}</th>
                  <th className="text-right">{t("instruments.tick")}</th>
                  <th className="text-right">{t("instruments.lot")}</th>
                  <th className="text-right">{t("instruments.priceMin")}</th>
                  <th className="text-right">{t("instruments.priceMax")}</th>
                  <th>{t("common.updated")}</th>
                </tr>
              </thead>
              <tbody>
                {eligible.map((i) => {
                  const checked = allowlistMode ? selectedSet.has(i.market_id) : true;
                  return (
                    <tr
                      key={i.market_id}
                      className={allowlistMode && !checked ? "opacity-60" : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t("instruments.includeAria", { id: i.market_id })}
                          checked={checked}
                          disabled={!preset || !allowlistMode || mut.isPending}
                          onChange={() => toggleInstrument(i.market_id)}
                        />
                      </td>
                      <td className="mono">{i.market_id}</td>
                      {/* Every mode this market can be quoted in, not
                          just the preset's: the same market being
                          selectable in a separate Game preset is the
                          supported shape, and hiding that makes the
                          two presets look like a mistake. */}
                      <td>
                        <span
                          className="flex flex-wrap items-center gap-1"
                          aria-label={t("instruments.modeTagAria")}
                        >
                          {marketModes(i).map((m) => (
                            <TradingModeBadge key={m} mode={m} />
                          ))}
                        </span>
                      </td>
                      {/* SM-461: price columns read on the unitless 0–1
                          scale (SM-460 matrix, SM-986), not raw wire
                          integers. Lot size stays a raw amount-minor
                          quantity. */}
                      <td className="amt text-right">{formatPrice(i.tick_size, i.tick_size)}</td>
                      <td className="amt text-right">{i.lot_size}</td>
                      <td className="amt text-right">{formatPrice(i.price_min, i.tick_size)}</td>
                      <td className="amt text-right">{formatPrice(i.price_max, i.tick_size)}</td>
                      <td className="text-xs t-mute">
                        {i.updated_at ? formatDateTime(i.updated_at) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {mut.isError && (
        <Card>
          <p className="text-xs" style={{ color: "var(--no)" }}>
            {t("instruments.saveFailed", { message: (mut.error as Error).message })}
          </p>
        </Card>
      )}
    </div>
  );
}
